import type React from "react";

/**
 * Arrow-key roving focus for a `role="tablist"`, per the WAI-ARIA tabs
 * pattern: Left/Right/Home/End move focus AND activate, the way browser tab
 * strips and most OS widgets already behave. Reads siblings from the DOM
 * (closest tablist ancestor) rather than threading an ordered value list
 * through each call site's own state -- every tab button needs a
 * `data-value` attribute matching what `onActivate` expects.
 */
export function handleTabListArrowKeys(
  e: React.KeyboardEvent<HTMLButtonElement>,
  onActivate: (value: string) => void,
): void {
  const list = e.currentTarget.closest('[role="tablist"]');
  if (!list) return;
  const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  const currentIndex = tabs.indexOf(e.currentTarget);
  if (currentIndex === -1) return;

  let nextIndex: number | null = null;
  if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
  else if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  else if (e.key === "Home") nextIndex = 0;
  else if (e.key === "End") nextIndex = tabs.length - 1;
  if (nextIndex === null) return;

  e.preventDefault();
  const nextTab = tabs[nextIndex];
  const nextValue = nextTab?.dataset["value"];
  if (nextTab && nextValue !== undefined) {
    nextTab.focus();
    onActivate(nextValue);
  }
}
